import { useState } from "react";
import { Download, Eye, FileIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "src/components/dialogue/radix-dialog";
import { VIEW_MEDIA_URL } from "src/redux/api/appConst";

const getFileType = (url?: string): "image" | "pdf" | "other" => {
  if (!url) return "other";
  const ext = url.split(".").pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext || "")) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
};

export const DocumentPreview = ({ url }: { url: string }) => {
  const [open, setOpen] = useState(false);
  const fileType = getFileType(url);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="group relative flex items-center gap-3 w-full rounded-xl border border-gray-200 p-2 hover:border-blue-300 transition"
      >
        {fileType === "image" ? (
          <img
            src={`${VIEW_MEDIA_URL}/${url}`}
            alt="Uploaded document"
            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center border border-gray-200">
            <FileIcon className="w-5 h-5 text-red-500" />
          </div>
        )}
        <div className="flex-1 text-left">
          <p className="text-xs font-medium text-gray-800">
            Supporting Document
          </p>
          <p className="text-[11px] text-gray-500">
            Click to preview
          </p>
        </div>
        <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader className="flex-row items-center justify-between">
            <DialogTitle>Supporting Document</DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-center bg-gray-50 rounded-xl min-h-[300px] max-h-[70vh] overflow-auto">
            {fileType === "image" ? (
              <img
                src={`${VIEW_MEDIA_URL}/${url}`}
                alt="Uploaded document"
                className="max-w-full max-h-[65vh] object-contain rounded-lg"
              />
            ) : fileType === "pdf" ? (
              <iframe
                src={`${VIEW_MEDIA_URL}/${url}`}
                className="w-full h-[65vh] rounded-lg"
                title="Document preview"
              />
            ) : (
              <div className="text-center p-6">
                <FileIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  Preview not available for this file type
                </p>
              </div>
            )}
          </div>

          <a
            href={`${VIEW_MEDIA_URL}/${url}`}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 transition"
          >
            <Download className="w-4 h-4" />
            Download
          </a>
        </DialogContent>
      </Dialog>
    </>
  );
};
